<script setup>
import {useRoute} from "vue2-helpers/vue-router";
import {ref, onMounted, provide, computed} from "vue";
import needsStartupsService from "@/services/needs-startups.service";
import SearchMatching from "@/views/needs/need/SearchMatching.vue";
import Solutions from "@/views/needs/need/solutions/Solutions.vue";
import NewMatches from "@/views/needs/need/NewMatches.vue";
import VClamp from "vue-clamp";
import Activities from "@/views/needs/need/Activities.vue";
import NeedEditModal from "@/views/needs/need/NeedEditModal.vue";
import Escalate from "@/views/needs/need/Escalate.vue";
import NeedReport from "@/views/needs/need/NeedReport.vue";
import needsItemService from "@/services/needs-item.service";
import {Avatar} from "vue-avatar/src";

const route = useRoute();
const need = ref(null);
const startups = ref([]);
const activities = ref([]);
const searchMatching = ref(null);
const needEditModal = ref(null);
const newMatches = ref(null);
const isSharedWithEcosystems = ref(null);
const startupsLoaded = ref(false);

const solutions = computed(() => startups.value.filter(startup => startup.status === 'liked'));
const unreviewedStartups = computed(() => startups.value.filter(startup => !startup.status));

const escalationDate = computed(() => {
    const date = new Date(need.value.escalation_date);

    return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
    });
});

onMounted(() => {
    getInfo();
});

async function getInfo() {
    await getNeedInfo();

    if (!need.value.start_search) {
        getStartups();
        getActivities();
    }
}

async function getNeedInfo() {
    const {data} = await needsItemService.getInfo(route.params.id);

    need.value = data;
    isSharedWithEcosystems.value = !!need.value.is_shared_with_ecosystems;
}

async function getWorkStatus() {
    const {data} = await needsItemService.getWorkStatus(need.value.id);

    need.value.work_status = data;
}

async function getActivities() {
    const {data} = await needsItemService.getActivities(need.value.id);

    activities.value = data;
}

async function getStartups() {
    const {data} = await needsStartupsService.getStartups(need.value.id);
    startups.value = data;
    startupsLoaded.value = true;
}

function updateRating(linkId, rating) {
    const startup = startups.value.find(s => s.link_id === linkId);

    if (startup) {
        startup.rating = rating;
    }

    getWorkStatus();
}

async function setEcosystemsVisibility(type) {
    await needsItemService.setEcosystemsVisibility(need.value.id, type);
    getInfo();
}

function evaluate() {
    newMatches.value?.evaluate();
}

provide('need', need);
provide('getInfo', getInfo);
provide('getWorkStatus', getWorkStatus);
provide('getStartups', getStartups);
provide('updateRating', updateRating);
provide('getActivities', getActivities);
provide('getNeedInfo', getNeedInfo);
provide('solutions', solutions);
provide('unreviewedStartups', unreviewedStartups);
</script>

<template>
    <div v-if="need" class="need-page">
        <div class="need-page__content">
            <aside class="need-page__aside">
                <h4 class="heading4">{{need.name}}</h4>

                <p>
                    <v-clamp autoresize :max-lines="4" class="activity-list__content__text" style="white-space: pre-wrap">
                        {{need.description}}

                        <template #after="{ toggle, expanded, clamped }">
                            <a v-if="expanded" class="link-normal" @click="toggle">less</a>
                            <a v-if="clamped" class="link-normal" @click="toggle">more</a>
                        </template>
                    </v-clamp>
                </p>

                <div v-if="!need.escalated" class="mb-4">
                    <a class="link link-edit-light" @click="needEditModal.open(need.id)">Refine</a>
                </div>

                <Escalate class="mb-4"/>

                <NeedReport v-if="solutions.length >= 5"/>

                <div v-if="activities.length" class="card-headline">
                    <header class="card-headline__header">Activity</header>

                    <Activities
                        :activities="activities"
                    />
                </div>

                <div v-if="!need.escalated" class="card-headline">
                    <header class="card-headline__header">Visibility Settings</header>

                    <div class="card-headline__content">
                        <div class="input-radio mb-4">
                            <input
                                type="radio"
                                v-model="isSharedWithEcosystems"
                                :value="true"
                                name="sinp"
                                id="s1"
                                @change="setEcosystemsVisibility(true)"
                            >
                            <label for="s1">
                                <div class="input-radio__inner">
                                    AI + Network Partners (Recommended)
                                    <p>Get insights from SwitchPitch AI and our venture ecosystem partners</p>
                                </div>
                            </label>
                        </div>

                        <div class="input-radio">
                            <input
                                type="radio"
                                v-model="isSharedWithEcosystems"
                                :value="false"
                                name="sinp"
                                id="s2"
                                @change="setEcosystemsVisibility(false)"
                            >
                            <label for="s2">
                                <div class="input-radio__inner">
                                    AI Only
                                    <p>Your request will not be shared with ecosystem partners</p>
                                </div>
                            </label>
                        </div>                        
                    </div>
                </div>
            </aside>

            <SearchMatching
                v-if="need?.need_matching_search"
                :need-id="need.id"
                ref="searchMatching"
                @done="getInfo"
            />

            <div v-else-if="startupsLoaded" class="need-page__inner">
                <template v-if="need.escalated">
                    <div v-if="need.tracker_id" class="card-needs mb-4">
                        <div class="card-needs__content" style="display: flex; align-items: center; justify-content: space-between;">
                            <div>
                                This need has been converted to tracker
                            </div>

                            <div class="dropdown-airy dropdown-airy--share">
                                <a class="btn button-tracker" :href="`/trackers/${need.tracker_id}`" target="_blank">Open tracker</a>
                            </div>
                        </div>
                    </div>

                    <div class="card-needs mb-4">
                        <div class="card-needs__content">
                            <div class="card-needs__content__header">
                                <div class="need-plate--user">
                                    <div class="need-plate--user__image">
                                        <Avatar
                                            :size="20"
                                            :src="need.creator_avatar"
                                            :username="need.creator_name"
                                        />
                                    </div>

                                    <div class="need-plate--user__inner">{{need.creator_name}}</div>
                                </div>

                                Escalated need on {{ escalationDate }}
                            </div>

                            <v-clamp autoresize :max-lines="5" style="white-space: pre-wrap" v-if="need.escalation_comment" class="card-needs__panel">
                                {{need.escalation_comment}}

                                <template #after="{ toggle, expanded, clamped }">
                                    <a v-if="expanded" class="link-normal" @click="toggle">less</a>
                                    <a v-if="clamped" class="link-normal" @click="toggle">more</a>
                                </template>
                            </v-clamp>
                        </div>
                    </div>
                </template>

                <NewMatches v-if="!need.escalated" ref="newMatches"/>

                <Solutions @evaluate="evaluate"/>
            </div>
        </div>

        <NeedEditModal ref="needEditModal"/>
    </div>
</template>

<style scoped>

</style>
